<template>
  <div class="flex h-screen bg-background">
    <!-- Main Content -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- Top Navigation -->
      <header class="bg-secondary shadow-sm">
        <div class="max-w-[1440px] mx-auto">
          <div class="flex items-center justify-between p-4">
            <div class="flex items-center space-x-8">
              <!-- Logo -->
              <NuxtLink to="/" class="flex items-center">
                <img src="~/assets/img/loop-letters-logo.svg" class="h-[50px] w-[50px]" alt="Logo" />
              </NuxtLink>

              <!-- Main Navigation -->
              <nav class="hidden md:flex items-center space-x-8">
                <NuxtLink to="/dashboard" class="font-bold hover:text-text transition-colors duration-200" :class="[$route.path === '/dashboard' ? 'text-[#FF3C38]' : 'text-white']">
                  Dashboard
                </NuxtLink>
                <NuxtLink to="/campaigns" class="font-bold hover:text-text transition-colors duration-200" :class="[$route.path.startsWith('/campaigns') ? 'text-[#FF3C38]' : 'text-white']">
                  Campaigns
                </NuxtLink>
                <NuxtLink to="/templates" class="font-bold hover:text-text transition-colors duration-200" :class="[$route.path.startsWith('/templates') ? 'text-[#FF3C38]' : 'text-white']">
                  Templates
                </NuxtLink>
                <NuxtLink to="/contacts" class="font-bold hover:text-text transition-colors duration-200" :class="[$route.path.startsWith('/contacts') ? 'text-[#FF3C38]' : 'text-white']">
                  Contacts
                </NuxtLink>
                <NuxtLink to="/analytics" class="font-bold hover:text-text transition-colors duration-200" :class="[$route.path.startsWith('/analytics') ? 'text-[#FF3C38]' : 'text-white']">
                  Analytics
                </NuxtLink>
                
                <!-- Settings Dropdown -->
                <div class="relative settings-nav-menu">
                  <button 
                    @click.stop="toggleSettingsNavMenu" 
                    class="flex items-center font-bold hover:text-text transition-colors duration-200"
                    :class="[$route.path.startsWith('/settings') ? 'text-[#FF3C38]' : 'text-white']"
                    aria-label="Settings menu"
                  >
                    <span>Settings</span>
                    <svg 
                      :class="['ml-1 h-5 w-5 transform transition-transform', isSettingsNavMenuOpen ? 'rotate-180' : '']"
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                    >
                      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>
                  
                  <!-- Settings dropdown items -->
                  <div 
                    v-if="isSettingsNavMenuOpen" 
                    class="absolute left-0 z-10 mt-2 w-48 origin-top-left rounded-md bg-secondary py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                    role="menu" 
                    aria-orientation="vertical"
                  >
                    <NuxtLink to="/settings" class="block px-4 py-2 text-sm font-bold hover:bg-secondary-light hover:text-text" :class="[$route.path === '/settings' ? 'text-[#FF3C38]' : 'text-white']">All Settings</NuxtLink>
                    <NuxtLink to="/settings/profile" class="block px-4 py-2 text-sm font-bold hover:bg-secondary-light hover:text-text" :class="[$route.path === '/settings/profile' ? 'text-[#FF3C38]' : 'text-white']">Your Profile</NuxtLink>
                    <NuxtLink to="/settings/domain" class="block px-4 py-2 text-sm font-bold hover:bg-secondary-light hover:text-text" :class="[$route.path === '/settings/domain' ? 'text-[#FF3C38]' : 'text-white']">Domains</NuxtLink>
                    <NuxtLink to="/settings/subscription" class="block px-4 py-2 text-sm font-bold hover:bg-secondary-light hover:text-text" :class="[$route.path === '/settings/subscription' ? 'text-[#FF3C38]' : 'text-white']">Billing</NuxtLink>
                  </div>
                </div>
              </nav>            </div>

            <!-- Right Side Controls -->
            <div class="flex items-center space-x-4 ml-2 md:ml-0">
              <!-- Start Campaign Button - Only show on larger screens if no campaigns yet -->
              <NuxtLink 
                v-if="!hasCampaigns && !campaignsLoading" 
                to="/campaigns/wizard" 
                class="hidden md:flex bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md items-center text-[13px]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="md:h-5 md:w-5 h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                </svg>
                <span class="md:inline inline">Start Your First Campaign</span>
              </NuxtLink>
              
              <!-- Create Campaign Button - Only show on larger screens if user has campaigns -->
              <NuxtLink 
                v-if="hasCampaigns && !campaignsLoading" 
                to="/campaigns/wizard" 
                class="hidden md:flex bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md items-center text-[13px]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="md:h-5 md:w-5 h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                </svg>
                <span class="md:inline inline">Create Campaign</span>
              </NuxtLink>
              
              <!-- Notification Bell -->
              <div class="relative">
                <button class="p-2 rounded-full bg-primary hover:bg-primary-dark flex items-center justify-center h-10 w-10">
                  <span class="sr-only">Notifications</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </button>
              </div>

              <!-- User Menu -->
              <div class="relative flex items-center user-menu-container">
                <button 
                  @click="toggleUserMenu" 
                  class="flex items-center p-2 rounded-full hover:bg-secondary-light text-text-secondary hover:text-text"
                  aria-label="Profile"
                >
                  <span class="sr-only">Profile</span>
                  <div v-if="user?.user_metadata?.avatar_url" class="h-10 w-10 rounded-full overflow-hidden">
                    <img :src="user.user_metadata.avatar_url" alt="User avatar" class="h-full w-full object-cover" />
                  </div>
                  <div v-else class="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white text-lg font-semibold">
                    {{ getInitials(user) }}
                  </div>
                </button>
                
                <!-- User info (visible on larger screens) -->
                <div class="hidden md:block ml-2 text-left">
                  <div class="text-text font-medium">{{ user?.user_metadata?.full_name || user?.email?.split('@')[0] }}</div>
                  <div class="text-text-secondary text-sm truncate max-w-[150px]">{{ user?.email }}</div>
                </div>

                <!-- User dropdown menu -->
                <div v-if="isUserMenuOpen" 
                     class="absolute right-0 top-[100%] z-10 mt-1 w-48 origin-top-right rounded-md bg-secondary py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                     role="menu" 
                     aria-orientation="vertical"
                     @keydown.escape="closeUserMenu">
                  <div class="px-4 py-2 text-sm text-text border-b border-secondary-light">
                    <div class="font-medium">{{ user?.user_metadata?.full_name || user?.email }}</div>
                    <div class="text-text-secondary truncate">{{ user?.email }}</div>
                  </div>
                  <NuxtLink to="/settings/profile" class="block px-4 py-2 text-sm text-text-secondary hover:bg-secondary-light hover:text-text">Your Profile</NuxtLink>
                  <NuxtLink to="/settings/domain" class="block px-4 py-2 text-sm text-text-secondary hover:bg-secondary-light hover:text-text">Domains</NuxtLink>
                  <NuxtLink to="/settings/subscription" class="block px-4 py-2 text-sm text-text-secondary hover:bg-secondary-light hover:text-text">Billing</NuxtLink>
                  <NuxtLink to="/settings" class="block px-4 py-2 text-sm text-text-secondary hover:bg-secondary-light hover:text-text">All Settings</NuxtLink>
                  <button @click="handleLogout" class="block w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-secondary-light hover:text-text">Sign out</button>
                </div>
              </div>

              <!-- Mobile Menu Button (visible only on mobile) -->
              <button 
                @click="toggleMobileMenu" 
                class="md:hidden p-2 rounded-md hover:bg-secondary-light text-text-secondary hover:text-text"
                aria-label="Toggle mobile menu">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Mobile Navigation Menu (visible only when toggled on mobile) -->
          <div v-if="isMobileMenuOpen" class="md:hidden bg-secondary border-t border-secondary-light">
            <nav class="px-4 py-2 space-y-1">
              <!-- Start Campaign Button (Mobile) - Only show if no campaigns yet -->
              <NuxtLink 
                v-if="!hasCampaigns && !campaignsLoading" 
                to="/campaigns/wizard" 
                class="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md flex items-center text-sm w-full mb-2 font-bold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                </svg>
                <span>Start Your First Campaign</span>
              </NuxtLink>
              
              <!-- Create Campaign Button (Mobile) - Only show if user has campaigns -->
              <NuxtLink 
                v-if="hasCampaigns && !campaignsLoading" 
                to="/campaigns/wizard" 
                class="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md flex items-center text-sm w-full mb-2 font-bold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                </svg>
                <span>Create Campaign</span>
              </NuxtLink>
              
              <NuxtLink to="/" class="block py-2 font-bold hover:text-text" :class="[$route.path === '/' ? 'text-[#FF3C38]' : 'text-white']">
                Dashboard
              </NuxtLink>
              <NuxtLink to="/campaigns" class="block py-2 font-bold hover:text-text" :class="[$route.path.startsWith('/campaigns') ? 'text-[#FF3C38]' : 'text-white']">
                Campaigns
              </NuxtLink>
              <NuxtLink to="/templates" class="block py-2 font-bold hover:text-text" :class="[$route.path.startsWith('/templates') ? 'text-[#FF3C38]' : 'text-white']">
                Templates
              </NuxtLink>
              <NuxtLink to="/contacts" class="block py-2 font-bold hover:text-text" :class="[$route.path.startsWith('/contacts') ? 'text-[#FF3C38]' : 'text-white']">
                Contacts
              </NuxtLink>
              <NuxtLink to="/analytics" class="block py-2 font-bold hover:text-text" :class="[$route.path.startsWith('/analytics') ? 'text-[#FF3C38]' : 'text-white']">
                Analytics
              </NuxtLink>
              <NuxtLink to="/settings" class="block py-2 font-bold hover:text-text" :class="[$route.path === '/settings' ? 'text-[#FF3C38]' : 'text-white']">
                Settings
              </NuxtLink>
              <NuxtLink to="/settings/profile" class="block py-2 pl-4 font-bold hover:text-text" :class="[$route.path === '/settings/profile' ? 'text-[#FF3C38]' : 'text-white']">
                ↳ Your Profile
              </NuxtLink>
              <NuxtLink to="/settings/domain" class="block py-2 pl-4 font-bold hover:text-text" :class="[$route.path === '/settings/domain' ? 'text-[#FF3C38]' : 'text-white']">
                ↳ Domains
              </NuxtLink>
              <NuxtLink to="/settings/subscription" class="block py-2 pl-4 font-bold hover:text-text" :class="[$route.path === '/settings/subscription' ? 'text-[#FF3C38]' : 'text-white']">
                ↳ Billing
              </NuxtLink>
            </nav>
          </div>
        </div>
      </header>

      <!-- Main Content Area -->
      <main class="flex-1 overflow-y-auto bg-background">
        <div class="max-w-[1440px] mx-auto p-6">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { UserCircleIcon } from '@heroicons/vue/24/solid';
import useAuth from '~/composables/useAuth';
import useCampaigns from '~/composables/useCampaigns';

// Get app title from the plugin
const { $appTitle } = useNuxtApp();

// Get user and auth functions
const { user, logout, initialize } = useAuth();

// Get campaign data
const { hasCampaigns, loading: campaignsLoading, fetchCampaigns } = useCampaigns();

// Get current route
const route = useRoute();

// Track menu states
const isUserMenuOpen = ref(false);
const isMobileMenuOpen = ref(false);
const isSettingsNavMenuOpen = ref(false);

// Determine page name based on current route
const pageName = computed(() => {
  // Map of routes to page names
  if (route.path === '/') return 'Home';
  if (route.path === '/dashboard') return 'Dashboard';
  if (route.path.startsWith('/campaigns')) {
    if (route.path === '/campaigns') return 'Campaigns';
    if (route.path === '/campaigns/wizard') return 'Create Campaign';
    return 'Campaign Details';
  }
  if (route.path.startsWith('/templates')) return 'Templates';
  if (route.path.startsWith('/contacts')) return 'Contacts';
  if (route.path.startsWith('/analytics')) return 'Analytics';
  if (route.path.startsWith('/settings')) {
    if (route.path === '/settings') return 'Settings';
    if (route.path === '/settings/profile') return 'Your Profile';
    if (route.path === '/settings/domain') return 'Domains';
    if (route.path === '/settings/subscription') return 'Billing';
    return 'Settings';
  }
  
  // Fallback to route path if no match
  return route.path.substring(1).charAt(0).toUpperCase() + route.path.substring(2);
});

// Set dynamic page title
useHead({
  title: computed(() => `${pageName.value} - ${$appTitle || 'Loop Letters'}`)
});

// Toggle user menu
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
  if (isUserMenuOpen.value) {
    isMobileMenuOpen.value = false;
    isSettingsNavMenuOpen.value = false;
  }
};

// Toggle settings menu in nav
const toggleSettingsNavMenu = () => {
  isSettingsNavMenuOpen.value = !isSettingsNavMenuOpen.value;
  if (isSettingsNavMenuOpen.value) {
    isUserMenuOpen.value = false;
  }
};

// Toggle mobile menu
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  if (isMobileMenuOpen.value) {
    isUserMenuOpen.value = false;
    isSettingsNavMenuOpen.value = false;
  }
};

// Close user menu
const closeUserMenu = () => {
  isUserMenuOpen.value = false;
};

// Handle logout
const handleLogout = async () => {
  const { success } = await logout();
  closeUserMenu();
  
  if (success) {
    // console.log('Logout successful, redirecting to login page');
    // Navigate to login page after successful logout
    navigateTo('/login', { replace: true });
  }
};

// Close menus when clicking outside
const closeMenusOnOutsideClick = (e) => {
  // Close user menu when clicking outside
  if (isUserMenuOpen.value && !e.target.closest('.user-menu-container')) {
    isUserMenuOpen.value = false;
  }

  // Close settings nav menu when clicking outside
  if (isSettingsNavMenuOpen.value && !e.target.closest('.settings-nav-menu')) {
    isSettingsNavMenuOpen.value = false;
  }

  // Close mobile menu when clicking outside
  if (isMobileMenuOpen.value && 
      !e.target.closest('button[aria-label="Toggle mobile menu"]') && 
      !e.target.closest('.md\\:hidden.bg-secondary')) {
    isMobileMenuOpen.value = false;
  }
};

// Single onMounted hook
onMounted(async () => {
  // Initialize auth
  await initialize();
  // console.log('Auth initialized, user:', user.value);
  
  // Fetch campaigns to check if user has any
  await fetchCampaigns();
  
  // Debug user data
  if (user.value) {
    // console.log('User email:', user.value.email);
    // console.log('User metadata:', user.value.user_metadata);
  }

  document.addEventListener('click', closeMenusOnOutsideClick);
});

onUnmounted(() => {
  document.removeEventListener('click', closeMenusOnOutsideClick);
});

// Function to get initials from user
const getInitials = (user) => {
  if (!user) return '?';
  
  // Try to get full name from user metadata
  const fullName = user.user_metadata?.full_name;
  if (fullName) {
    const nameParts = fullName.split(' ').filter(part => part.length > 0);
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    } else if (nameParts.length === 1) {
      return nameParts[0][0].toUpperCase();
    }
  }
  
  // Fallback to email
  const email = user.email;
  if (email) {
    return email[0].toUpperCase();
  }
  
  return '?';
};
</script> 